import {
  cardTypesForCreditCard,
  cardTypesForGooglePay,
  paymentOptions,
} from '@/common/constants/FormConstants';
import {
  BLANK_ERROR_MESSAGE,
  CARD_TYPE_VALIDATION_MESSAGE,
  EMPTY_FIRST_NAME_VALIDATION_MESSAGE,
  EMPTY_LAST_FOUR_DIGIT_VALIDATION_MESSAGE,
  EMPTY_LAST_NAME_VALIDATION_MESSAGE,
  FIND_TICKETS_API,
  INVALID_DATE_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
  PAYMENT_TYPE_PAYPAL,
  PURCHASE_DATE_VALIDATION_MESSAGE,
  VALID_LAST_FOUR_DIGIT_MESSAGE,
} from '@/common/constants/constants';
import Captcha from '@/components/Captcha/Captcha';
import ContactOrganizerInfo from '@/components/ContactOrganizer/ContactOrganizerInfo/ContactOrganizerInfo';
import DatePicker from '@/components/DatePicker/Datepicker';
import EventTypeRadioButton from '@/components/EventTypeRadios/EventTypeRadioButton';
import MaximumAttempReached from '@/components/MaximumAttempReached/MaximumAttempReached';
import Dropdown from '@/components/PaymentMethodDropdown/Dropdown';
import NameInformation from '@/components/PersonalInformation/NameInformation/NameInformation';
import PaypalIdDetails from '@/components/PersonalInformation/PaypalDetails/PaypalDetails';
import OnlineEvent from '@/pages/OnlineEvent.page';
import { useCSRFToken } from '@/pages/contextProvider';
import Help from '@/pages/help.page';
import homeStyles from '@/styles/Home.module.css';
import styles from '@/styles/IDontKnowEmail.module.scss';
import type { EventMode } from '@/types';
import { Order } from '@/types';
import { HEADER_NAME } from '@eventbrite/csrf-double-cookie';
import { Button, Divider, LoadingButton, TextField, Typography } from '@eventbrite/marmalade';
import React, { useState } from 'react';
import { InputsDontMatch } from '../InputsDontMatch/InputsDontMatch';
import { PageDisclaimer } from '../Layout/LeftPane/PageDisclaimer';
import { PageTitle } from '../Layout/LeftPane/PageTitle';
import { trackSubmitClick, trackTicketsFound, trackTicketsNotFound } from './SearchFormHeap';

interface SearchForm {
  setCurrentPage: (
    page: 'emailVerify' | 'iDontKnowEmail' | 'ticketsFound' | 'noAccountFound',
  ) => void;
  handleTicketsFound: (foundOrders: Order[]) => void;
}

const SearchForm: React.FC<SearchForm> = ({ setCurrentPage, handleTicketsFound }) => {
  const csrfToken = useCSRFToken();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [selectedCardTypeForCreditCard, setSelectedCardTypeForCreditCard] = useState<string | null>(
    null,
  );
  const [selectedCardTypeForGooglePay, setSelectedCardTypeForGooglePay] = useState<string | null>(
    null,
  );

  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [isHelpPage, setIsHelpPage] = useState<boolean>(false);
  const [showInputsDontMatch, setShowInputsDontMatch] = useState<boolean>(false);
  const [eventType, setEventType] = useState<EventMode | null>(null);
  const [lastFourDigits, setLastFourDigits] = useState<string>('');
  const [purchaseDate, setPurchaseDate] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [firstName, setfirstName] = useState<string>('');
  let [cardType, setCardType] = useState<string>('') || '';
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaSolved, setIsCaptchaSolved] = useState(false);
  const [captchaValidated, setCaptchaValidated] = useState<boolean>(true);
  const [lastFourDigitError, setLastFourDigitError] = useState('') || '';
  const [datePickerValidation, setDatePickerValidation] = useState('') || '';
  const [cardTypeValidation, setCardTypeValidation] = useState('') || '';
  const [lastNameError, setLastNameError] = useState('') || '';
  const [firstNameError, setFirstNameError] = useState('') || '';
  const [paypalTransactionIdError, setPaypalTransactionIdError] = useState('') || '';
  const [paypalInvoiceId, setPaypalInvoiceId] = useState<string>('');
  const [paypalInvoiceIdError, setPaypalInvoiceIdError] = useState('');
  const [orderId, setOrderId] = useState<string>('');

  const transformDate = (dateString: string): string => {
    const [month, day, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  const validatePaypalInvoiceId = (value: string): boolean => {
    const regex = /^\d+-\d+$/;
    return regex.test(value);
  };

  const handleLastFourDigitChange = (value: string) => {
    setLastFourDigits(value);
    if (value.length > 4) {
      setLastFourDigitError(VALID_LAST_FOUR_DIGIT_MESSAGE);
    }
  };

  const handlePaypalInvoiceChange = (value: string) => {
    setPaypalInvoiceIdError('');
    setPaypalInvoiceId(value);
    const orderId = value.split('-')[1];
    setOrderId(orderId);
  };

  const validateForm = () => {
    let validationFlag = true;

    if (!lastName || lastName.length < 2 || lastName.length > 30) {
      setLastNameError(EMPTY_LAST_NAME_VALIDATION_MESSAGE);
      validationFlag = false;
    }
    if (!firstName || firstName.length < 2 || firstName.length > 30) {
      setFirstNameError(EMPTY_FIRST_NAME_VALIDATION_MESSAGE);
      validationFlag = false;
    }

    if (!purchaseDate) {
      setDatePickerValidation(PURCHASE_DATE_VALIDATION_MESSAGE);
      validationFlag = false;
    } else if (purchaseDate.length < 10) {
      setDatePickerValidation(INVALID_DATE_ERROR_MESSAGE);
      validationFlag = false;
    }

    if (!cardType) {
      setCardTypeValidation(CARD_TYPE_VALIDATION_MESSAGE);
      validationFlag = false;
    }
    if (!isCaptchaSolved) {
      setCaptchaValidated(false);
      validationFlag = false;
    }
    if (selectedPaymentMethod === PAYMENT_TYPE_PAYPAL) {
      if (!paypalInvoiceId) {
        setPaypalInvoiceIdError('PayPal Invoice ID is required');
        validationFlag = false;
      } else if (!validatePaypalInvoiceId(paypalInvoiceId)) {
        setPaypalInvoiceIdError('Invalid format. Please enter in the valid format');
        validationFlag = false;
      }
    } else {
      if (!lastFourDigits) {
        setLastFourDigitError(EMPTY_LAST_FOUR_DIGIT_VALIDATION_MESSAGE);
        validationFlag = false;
      } else if (lastFourDigits.length !== 4) {
        setLastFourDigitError(VALID_LAST_FOUR_DIGIT_MESSAGE);
        validationFlag = false;
      }
    }

    return validationFlag;
  };

  const handleOnSubmitClick = async (event: React.FormEvent) => {
    trackSubmitClick();
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    cardType = cardType === 'VISA_DEBIT' ? 'VISA' : cardType;
    cardType = cardType === 'MASTERCARD_DEBIT' ? 'MASTERCARD' : cardType;

    setIsLoading(true);
    const requestBody = {
      first_name: firstName,
      last_name: lastName,
      last_four: lastFourDigits,
      order_id: orderId,
      card_type: cardType,
      payment_type: selectedPaymentMethod,
      purchase_date: transformDate(purchaseDate),
    };

    try {
      const response = await fetch(`${FIND_TICKETS_API}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [HEADER_NAME]: csrfToken,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(NETWORK_ERROR_MESSAGE);
      }

      const result = await response.json();

      if (Array.isArray(result) && result.length === 0) {
        setShowInputsDontMatch(true);
        trackTicketsNotFound();
        return;
      }

      trackTicketsFound();
      handleTicketsFound(result);
    } catch (error) {
      trackTicketsNotFound();
      setShowInputsDontMatch(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnIDontKnowClick = () => {
    setShowHelp(true);
    setIsHelpPage(false);
  };

  const handleOnINeedMoreHelpClick = () => {
    setShowHelp(true);
    setIsHelpPage(true);
  };

  const [maximumAttemptsReached, setMaximumAttemptsReached] = useState<boolean>(false);
  const isOnline = eventType === 'Online Event';
  const isInPerson = eventType === 'In-person Event';

  const renderFormFooter = (isDisabled: boolean) => (
    <>
      <div style={{ margin: '24px' }} />
      <Divider
        orientation="horizontal"
        decorative
        className={styles.divider}
        placeholder="Divider"
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
      <div style={{ margin: '24px' }} />
      <DatePicker
        date={purchaseDate}
        setDate={(date) => {
          setDatePickerValidation('');
          setPurchaseDate(date);
        }}
        isDisabled={isDisabled}
        validation={datePickerValidation}
      />
      <Button
        variant="secondary"
        className={styles['no-border-button']}
        data-testid="search-event-button-element"
        onClick={handleOnIDontKnowClick}
        data-heap-id="search_form_i_dont_know"
      >
        I don&apos;t know
      </Button>
      <Divider
        orientation="horizontal"
        decorative
        className={styles.divider}
        placeholder="Divider"
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
      <div style={{ margin: '24px' }} />
      <NameInformation
        first_name={firstName}
        last_name={lastName}
        setFirstName={(firstName) => {
          setFirstNameError(BLANK_ERROR_MESSAGE);
          setfirstName(firstName);
        }}
        setLastName={(lastName) => {
          setLastNameError(BLANK_ERROR_MESSAGE);
          setLastName(lastName);
        }}
        isDisabled={isDisabled}
        validatedLastName={lastNameError}
        validatedFirstName={firstNameError}
      />
      <div style={{ margin: '24px' }} />
      <Captcha setIsCaptchaSolved={setIsCaptchaSolved} validatedCaptcha={!!captchaValidated} />
      <div style={{ margin: '24px' }} />
      <LoadingButton
        isLoading={isLoading}
        size="block"
        variant="primary"
        className={styles.submit_button}
        onClick={handleOnSubmitClick}
        data-heap-id="search_form_submit"
      >
        Submit
      </LoadingButton>
      <div style={{ margin: '12px' }} />
      <Button
        variant="secondary"
        className={homeStyles['no-border-button']}
        data-testid="i-need-more-help-button-element"
        onClick={handleOnINeedMoreHelpClick}
        data-heap-id="search_form_i_need_more_help"
      >
        I need more help
      </Button>
    </>
  );

  const renderCardDetails = (
    cardTypes: { value: string; label: string }[],
    selectedCardType: string | null,
    setSelectedCardType: (option: string | null) => void,
    isDisabled: boolean,
  ) => (
    <div className="render-card-details">
      <div style={{ margin: '24px' }} />
      <Dropdown
        dropdownItems={cardTypes}
        placeholder="Type of card"
        selectedOption={selectedCardType}
        setSelectedOption={(option: string | null) => {
          setCardTypeValidation(BLANK_ERROR_MESSAGE);
          setSelectedCardType(option);
          setCardType(option || BLANK_ERROR_MESSAGE);
        }}
        isDisabled={isDisabled}
        validation={cardTypeValidation}
      />
      <div style={{ margin: '24px' }} />
      <TextField
        id="last_four_digits_of_card"
        label="Last 4 digits of card (Ex: 1234)"
        required
        type="number"
        value={lastFourDigits}
        onChange={(e) => {
          setLastFourDigitError(BLANK_ERROR_MESSAGE);
          handleLastFourDigitChange(e);
        }}
        hasError={!!lastFourDigitError}
        helperText={lastFourDigitError}
        crossOrigin={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        disabled={isDisabled}
      />
      {renderFormFooter(isDisabled)}
    </div>
  );

  const renderFormFields = (isDisabled: boolean) => {
    if (!selectedPaymentMethod) {
      return null;
    }

    const paymentMethodComponents: { [key: string]: JSX.Element } = {
      NONE: (
        <OnlineEvent infoText="For security reasons, Eventbrite can’t look up free tickets. If you" />
      ),
      "I don't know": (
        <ContactOrganizerInfo infoText="For security reasons, Eventbrite can’t provide tickets without this information. If you" />
      ),
      CREDIT_OR_DEBIT: renderCardDetails(
        cardTypesForCreditCard,
        selectedCardTypeForCreditCard,
        setSelectedCardTypeForCreditCard,
        isDisabled,
      ),
      GOOGLE_PAY_OR_APPLE_PAY: renderCardDetails(
        cardTypesForGooglePay,
        selectedCardTypeForGooglePay,
        setSelectedCardTypeForGooglePay,
        isDisabled,
      ),
      PAYPAL: (
        <>
          <PaypalIdDetails
            isDisabled={isDisabled}
            setLastFourDigits={(value) => {
              setPaypalTransactionIdError(BLANK_ERROR_MESSAGE);
              setLastFourDigitError(BLANK_ERROR_MESSAGE);
              handlePaypalInvoiceChange(value);
            }}
            setCardType={(type) => {
              setCardType(type);
            }}
            validatedTxnId={paypalInvoiceIdError}
          />
          {renderFormFooter(isDisabled)}
        </>
      ),
    };

    return paymentMethodComponents[selectedPaymentMethod] || null;
  };

  const handleTryAgain = () => {
    setShowInputsDontMatch(false);
    setIsCaptchaSolved(false);
    setCaptchaValidated(true);
  };

  return (
    <main className={styles['main-container']}>
      <div>
        {showInputsDontMatch ? (
          <>
            <InputsDontMatch onTryAgainClick={handleTryAgain} />
          </>
        ) : showHelp ? (
          <Help isHelpPage={isHelpPage} onGoBack={() => setShowHelp(false)} />
        ) : (
          <>
            <PageTitle pageTitle={'Find tickets'} />
            <div className={styles['inner-container']}>
              <Typography variant="body-md">
                For security reasons, Eventbrite must verify your identity. Please provide more
                information about your ticket order.
              </Typography>
              <form className={styles['form-container']} onSubmit={handleOnSubmitClick}>
                {maximumAttemptsReached && <MaximumAttempReached />}
                <EventTypeRadioButton
                  eventType={eventType}
                  setEventType={setEventType}
                  isDisabled={maximumAttemptsReached}
                />
                {!isInPerson && !isOnline && <PageDisclaimer />}
                {isInPerson && (
                  <div className="In-person">
                    <div className={styles['payment-method-dropdown']}>
                      <div style={{ margin: '24px' }} />
                      <Dropdown
                        dropdownItems={paymentOptions}
                        placeholder="Payment method"
                        selectedOption={selectedPaymentMethod}
                        setSelectedOption={setSelectedPaymentMethod}
                        isDisabled={maximumAttemptsReached}
                      />
                    </div>
                    {renderFormFields(maximumAttemptsReached)}
                    <PageDisclaimer />
                  </div>
                )}
                {isOnline && (
                  <OnlineEvent infoText="We're sorry, but we can't look up tickets for online events. If you" />
                )}
              </form>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default SearchForm;
